import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { CreatePostTypeMultiple } from "../../types/ComponentProps";

import PostInterActionBlk from "./postInterAction/PostInterAction";
import Spinner from "../spinner/Spinner";

import PostSectionContainerStyles from "./PostSectionContainer.module.css";

import { usePostForm } from "../../context/PostFormContext";

import useGetAllPosts from "../../hooks/useGetAllPosts";
import useGetLeaderBoard from "../../hooks/useGetLeaderBoard";
import useGetUserPoint from "../../hooks/useGetUserPoint";
import useGetAllNotification from "../../hooks/useGetAllNotification";

import createPost from "../../api/createPost";

const initialPostState: CreatePostTypeMultiple = {
  message: "",
  imagePost: [],
  pointsGiven: 0,
  coinsGiven: 0,
  recipients: [],
  hashTagIds: [],
};

type Props = {
  addMoreInputRef?: React.RefObject<HTMLInputElement>;
};

const PostSectionFooter = ({ addMoreInputRef }: Props) => {
  const queryClient = useQueryClient();
  const { refetch } = useGetAllPosts();
  const { refetchLeaderBoardList } = useGetLeaderBoard();
  const { refetchGetUserPoint } = useGetUserPoint();
  const { refetch: refetchNotification } = useGetAllNotification();

  const { formPost, setFormPost, setMessage, setPostLoading } = usePostForm();

  const [postLoading, setPostListLoading] = useState<boolean>(false);

  useEffect(() => {
    const maxSizeInBytes = 5 * 1024 * 1024;

    if (!formPost?.imagePost || formPost.imagePost.length === 0) return;
    const validImages = formPost.imagePost.filter(
      (file: File) => file.size <= maxSizeInBytes,
    );

    if (validImages.length < formPost.imagePost.length) {
      setMessage("One or more images exceeded 5MB and were not added.");
      setFormPost((previous: any) => ({
        ...previous,
        imagePost: validImages,
      }));
    }
  }, [formPost?.imagePost]);

  const invalidateAllQueries = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["getAllPosts"] }),
      queryClient.invalidateQueries({ queryKey: ["getLeaderBoardList"] }),
      queryClient.invalidateQueries({ queryKey: ["getUserPoint"] }),
      queryClient.invalidateQueries({ queryKey: ["getNotificationById"] }),
      queryClient.invalidateQueries({ queryKey: ["getAllHashTag"] }),
      queryClient.invalidateQueries({
        queryKey: ["getAllHashTagListDashboard"],
      }),
    ]);

  const refetchAll = () =>
    Promise.all([
      refetch(),
      refetchLeaderBoardList(),
      refetchGetUserPoint(),
      refetchNotification(),
    ]);

  const createPostMutation = useMutation({
    mutationFn: createPost,
    onSuccess: async (postData) => {
      try {
        setFormPost(initialPostState);
        setPostLoading(true);
        await invalidateAllQueries();
        await refetchAll();
      } catch (error) {
        console.error("Error during post success handling:", error);
      } finally {
        setMessage("");
        setPostLoading(false);
        setPostListLoading(false);
      }
    },
    onError: (error: Error) => {
      console.error("Error creating post:", error);
      setMessage("");
      setPostLoading(false);
      setPostListLoading(false);
    },
  });

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (
      !formPost.message.trim() &&
      (!formPost.recipients || formPost.recipients.length === 0) &&
      (!formPost.hashTagIds || formPost.hashTagIds.length === 0) &&
      !formPost.pointsGiven
    ) {
      setMessage("Message is required to post");
      return;
    }
    if (
      formPost?.pointsGiven &&
      (!formPost?.recipients || formPost.recipients.length === 0)
    ) {
      setMessage(
        "Please select at least one recipient if you're giving points.",
      );
      return;
    }

    setPostListLoading(true);
    await createPostMutation.mutateAsync({
      ...formPost,
      imagePost: formPost.imagePost, // send the full array
    });
  };

  return (
    <div className={PostSectionContainerStyles.postSectionFooterBlk}>
      <PostInterActionBlk fileInputRef={addMoreInputRef} />
      <div>
        <button
          className={PostSectionContainerStyles.submitButton}
          type="submit"
          onClick={handleSubmit}
          disabled={formPost.imagePost.length > 6}
        >
          {postLoading ? (
            <Spinner alignSpin="center-spinner" variant="small-spinner" />
          ) : (
            <>
              <span>Post</span>
              <i className="fa-solid fa-arrow-right"></i>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PostSectionFooter;
